import { Box, Button, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

export interface ESignaturePadHandle {
    getSignatureDataUrl: () => string | null;
    clear: () => void;
}

interface ESignaturePadProps {
    signerName?: string;
    accentColor?: string;
    disabled?: boolean;
    onChange?: (state: { isValid: boolean }) => void;
}

const ESignaturePad = forwardRef<ESignaturePadHandle, ESignaturePadProps>(
    ({ signerName, accentColor = '#000000', disabled = false, onChange }, ref) => {
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);
        const isDrawingRef = useRef(false);
        const strokePointsRef = useRef<{ x: number; y: number }[]>([]);
        const [hasDrawn, setHasDrawn] = useState(false);

        const borderColor = useColorModeValue('gray.300', 'whiteAlpha.300');

        useEffect(() => {
            const canvas = canvasRef.current;
            const container = containerRef.current;
            if (!canvas || !container) return;

            const ratio = window.devicePixelRatio || 1;
            const { width, height } = container.getBoundingClientRect();
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            canvas.width = width * ratio;
            canvas.height = height * ratio;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.scale(ratio, ratio);
            ctx.lineWidth = 2.2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = accentColor;
        }, [accentColor]);

        const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
            const canvas = canvasRef.current;
            if (!canvas) return null;
            const rect = canvas.getBoundingClientRect();
            return { x: e.clientX - rect.left, y: e.clientY - rect.top };
        };

        const midPoint = (p1: { x: number; y: number }, p2: { x: number; y: number }) => ({
            x: (p1.x + p2.x) / 2,
            y: (p1.y + p2.y) / 2,
        });

        const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
            if (disabled) return;
            const canvas = canvasRef.current;
            if (!canvas) return;
            canvas.setPointerCapture(e.pointerId);
            isDrawingRef.current = true;
            const point = getPoint(e);
            strokePointsRef.current = point ? [point] : [];
        };

        const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
            if (disabled || !isDrawingRef.current) return;
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d');
            const point = getPoint(e);
            if (!ctx || !point) return;

            const points = strokePointsRef.current;
            points.push(point);

            // Smooth the stroke by drawing a quadratic curve through the midpoints
            // of the last three points, instead of straight segments between raw
            // pointer samples (which look jagged when the pointer moves fast).
            if (points.length < 3) {
                const [first] = points;
                ctx.beginPath();
                ctx.moveTo(first.x, first.y);
                ctx.lineTo(point.x, point.y);
                ctx.stroke();
            } else {
                const lastIndex = points.length - 1;
                const p1 = points[lastIndex - 2];
                const p2 = points[lastIndex - 1];
                const p3 = points[lastIndex];
                const start = midPoint(p1, p2);
                const end = midPoint(p2, p3);

                ctx.beginPath();
                ctx.moveTo(start.x, start.y);
                ctx.quadraticCurveTo(p2.x, p2.y, end.x, end.y);
                ctx.stroke();
            }

            if (!hasDrawn) {
                setHasDrawn(true);
                onChange?.({ isValid: true });
            }
        };

        const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
            isDrawingRef.current = false;
            strokePointsRef.current = [];
            const canvas = canvasRef.current;
            if (canvas && canvas.hasPointerCapture(e.pointerId)) {
                canvas.releasePointerCapture(e.pointerId);
            }
        };

        const handleClear = () => {
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d');
            if (canvas && ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
            setHasDrawn(false);
            onChange?.({ isValid: false });
        };

        useImperativeHandle(ref, () => ({
            getSignatureDataUrl: () => {
                if (!hasDrawn || !canvasRef.current) return null;
                return canvasRef.current.toDataURL('image/png');
            },
            clear: handleClear,
        }));

        return (
            <Box>
                <Box
                    ref={containerRef}
                    position="relative"
                    borderWidth="2px"
                    borderStyle="dashed"
                    borderColor={borderColor}
                    borderRadius="10px"
                    overflow="hidden"
                    bg="white"
                    h="180px"
                    opacity={disabled ? 0.6 : 1}
                >
                    <canvas
                        ref={canvasRef}
                        style={{ width: '100%', height: '100%', touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair' }}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                    />
                    {!hasDrawn && (
                        <Flex position="absolute" top={0} left={0} right={0} bottom={0} align="center" justify="center" pointerEvents="none">
                            <Text color="gray.300" fontSize="sm">Firma qui</Text>
                        </Flex>
                    )}
                </Box>
                <Flex justifyContent="space-between" align="center" mt={2}>
                    <Text fontSize="xs" color="gray.500">{signerName}</Text>
                    <Button
                        size="xs"
                        variant="ghost"
                        onClick={handleClear}
                        isDisabled={disabled || !hasDrawn}
                        _focus={{ boxShadow: 'none' }}
                    >
                        Cancella
                    </Button>
                </Flex>
            </Box>
        );
    }
);

ESignaturePad.displayName = 'ESignaturePad';

export default ESignaturePad;
